// Lógica pura del cruce CRM ↔ estado de cobro (Cobranza).
//
// Extraída de la vista para que la MISMA regla la apliquen tanto la tabla en
// pantalla como el export CSV (que parsea el CSV del backend en cliente). No
// depende de React: recibe los datos ya resueltos y devuelve la clasificación
// efectiva.

import {
  CollectionClassification,
  CollectionRow,
  PaymentIssueTicket,
} from "../../types/collections";

// Pipeline del CRM que marca una disputa SEPA (reclasifica la fila a "disputada").
export const SEPA_DISPUTE_PIPELINE = "disputados_sepa";

// Resultado del cruce CRM para una fila.
export type CrmCross = {
  tickets: PaymentIssueTicket[];
  // Clasificación forzada por el cruce, o null si el CRM no reclasifica.
  override: CollectionClassification | null;
  // El ticket contradice el estado de Stripe (impago en CRM pero pagado en
  // Stripe, o viceversa) → aviso destacado en la fila.
  conflict: boolean;
};

// Estados del backend que el cruce CRM NO reclasifica (solo enseña el ticket
// asociado, sin forzar dudosa/disputada ni marcar conflicto). Además de los
// terminales (cancelada/siniestro), "in_progress" (En trámite) es un onboarding
// aún en curso: sus impagos/envíos son churn esperado, no una anomalía de cobro.
function isTerminal(classification: string): boolean {
  return (
    classification === "canceled" ||
    classification === "claim" ||
    classification === "in_progress"
  );
}

// Override del CRM a partir de los tickets de una fila. Reglas:
//  - fila terminal (cancelada/siniestro/en trámite) → sin override.
//  - ticket en pipeline disputados_sepa → "disputada".
//  - resto de tickets de impago/disputa → al menos "dudosa".
export function crmOverrideFor(
  classification: string,
  tickets: PaymentIssueTicket[],
): CollectionClassification | null {
  if (tickets.length === 0) return null;
  if (isTerminal(classification)) return null;
  const hasSepaDispute = tickets.some(
    (t) => t.pipeline === SEPA_DISPUTE_PIPELINE,
  );
  return hasSepaDispute ? "disputed" : "doubtful";
}

// Clasificación efectiva tras el cruce: el override del CRM aplica salvo que
// Stripe ya la marque como impagada (ese estado manda sobre "dudosa"; la disputa
// SEPA sí reclasifica). Esta es la MISMA precedencia que pinta la fila.
export function effectiveClassification(
  classification: CollectionClassification,
  override: CollectionClassification | null,
): CollectionClassification {
  if (!override) return classification;
  if (classification === "unpaid" && override === "doubtful") return "unpaid";
  return override;
}

// Cruce completo de una fila cargada (tabla en pantalla): decide override y
// conflicto Stripe↔CRM. Devuelve null si la fila no tiene tickets asociados.
export function crossRow(
  row: CollectionRow,
  ticketsByLead: Map<string, PaymentIssueTicket[]>,
): CrmCross | null {
  const tickets = ticketsByLead.get(row.leadUlid);
  if (!tickets || tickets.length === 0) return null;

  const terminal = isTerminal(row.classification);
  const override = crmOverrideFor(row.classification, tickets);

  // ¿Stripe considera pagada / impagada alguna mensualidad de la ventana?
  const stripeSaysPaid = row.months.some((m) => m.status === "PAID");
  const stripeSaysUnpaid = row.months.some(
    (m) => m.status === "UNPAID" || m.status === "DISPUTED",
  );
  const conflict =
    !terminal &&
    ((stripeSaysPaid && !stripeSaysUnpaid) ||
      (stripeSaysUnpaid && row.classification === "perfect"));

  return { tickets, override, conflict };
}
